
import { GlobalContext } from '../../utils/globalprovider.js';
import SimplePopup from '../bases/popup/simplePopup.js';
import { keyboard_jsonToCsv } from '../../utils/keyboard_multi.js';
import React, {
  forwardRef,
  useContext,
  useImperativeHandle,
  useState,
  useMemo,
  useCallback,
  useEffect
} from 'react';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
} from '@tanstack/react-table';

import { first, flatMapDeep, sortBy } from 'lodash';

// A TanStack fork of Kent C. Dodds' match-sorter library that provides ranking information
import { rankings, rankItem } from '@tanstack/match-sorter-utils';

import './raccourcis.css';
import IndeterminateCheckbox from './components/indeterminate-checkbox.js';
import DebouncedInput from './components/debounced-input.js';
import SortIcon from './components/sort-icon.js';
import FormRaccourci from './components/form-raccourci.js';

// Define a custom fuzzy filter function that will apply ranking info to rows (using match-sorter utils)
const fuzzyFilter = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value, {
    threshold: rankings.CONTAINS,
  });

  // Store the itemRank info
  addMeta({
    itemRank,
  });

  // Return if the item should be filtered in/out
  return itemRank.passed;
};

// https://tanstack.com/table/latest/docs/guide/column-defs

const Raccourcis = forwardRef((props, ref) => {
  const context = useContext(GlobalContext);
  const lang = context.language;

  const formRaccourciRef = React.createRef();

  const [data, setData] = useState([]);
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState('');
  const [globalFilter, setGlobalFilter] = useState('');
  const [expanded, setExpanded] = useState({});
  const [grouping, setGrouping] = useState(['labelSubfamily']);
  const [shortcutsAreTesting, setShortcutsAreTesting] = useState(false);

  const [shortcutToUpdate, setShortCutToUpdate] = useState(undefined);
  const [updated, setUpdated] = useState(false);

  const [popup, setPopup] = useState({
    display: false,
    title: '',
    text: '',
    buttons: [],
    showIcon: false,
    icon: null,
  });

  const onClosePopup = () => {
    setPopup({
      display: false,
      title: '',
      text: '',
      buttons: [],
      showIcon: false,
      icon: null,
    });
  };

  useEffect(() => {
    if (props.default) {
      setSelectedApplication(props.default);
    }
  }, [props.default]);

  const shortcutsData = useMemo(() => {
    if (!data) {
      return;
    }

    const app = data.applications?.find((a) => a.name === selectedApplication);
    if (!app) return [];

    let rows = [];
    app.subfamily.forEach((sf) => {
      if (!sf.shortcuts || sf.shortcuts.length === 0) return;
      sf.shortcuts.forEach((sc) => {
        const current = sc.current; // voir le mac après
        const shortcut = [];
       
        if (current.ctrl) {
          shortcut.push('CTRL');
        }

         if (current.shift) {
          shortcut.push('SHIFT');
        }

        if (current.alt) {
          shortcut.push('ALT');
        }

        if (current.code === ' ') {
          current.code = lang === 'fr' ? 'Espace' : 'Space';
        }

        shortcut.push(current.code);

        rows.push({
          labelSubfamily: lang === 'fr' ? sf.fr : sf.en,
          key: sc.key,
          labelShortcut: lang === 'fr' ? sc.fr : sc.en,
          code: sc.current?.code || '',
          shift: sc.current?.shift || false,
          ctrl: sc.current?.ctrl || false,
          alt: sc.current?.alt || false,
          shortcut: shortcut.join(' + '),
        });
      });
    });
    return sortBy(rows, ['labelSubfamily', 'labelShortcut']);
  }, [selectedApplication, data, lang]);

  useImperativeHandle(ref, () => ({
    setRaccourcis(blob, updated = false) {
      setData(blob);

      const apps = blob.applications.map((x) => {
        return {
          name: x.name,
          fr: x.fr,
          en: x.en,
        };
      });
      setApplications(apps);
      setSelectedApplication(first(apps).name);
      setUpdated(updated);
    },

    onKeyDown(event) {
      if (event.target.id === 'txtRechercheRaccourcis') {
        event.preventDefault();
        event.stopPropagation();
        
        const setCursorPosition = (inputElement, position) => {
          setTimeout(() => {
            inputElement.setSelectionRange(position, position);
            inputElement.scrollLeft = inputElement.scrollWidth;
            inputElement.focus();
          }, 0);
        };

        const updateText = (input, value, start, end) => {
          setGlobalFilter(value);

          // Ensure input stays focused after update
          setTimeout(() => {
            setCursorPosition(input, start);
          }, 0);
        };

        const onBackspace = (input) => {
          let start = input.selectionStart;
          let end = input.selectionEnd;

          const text = globalFilter;
          const value = text.substring(0, start - 1) + text.substring(end);

          updateText(input, value, start - 1, end);
        };

        const onDelete = (input) => {
          let start = input.selectionStart;
          let end = input.selectionEnd;

          const text = globalFilter;
          const value = text.substring(0, start) + text.substring(end + 1);

          updateText(input, value, start, end);
        };

        const input = event.target;

        if (event.ctrlKey) {
          if (event.key === 'a') {
            input.setSelectionRange(0, input.value.length);
            return;
          }

          if (event.key === 'ArrowLeft') {
            let start = input.selectionStart;
            let end = input.selectionEnd;

            if (start === 0) return;

            input.setSelectionRange(start - 1, end);
            return;
          }

          if (event.key === 'ArrowRight') {
            let start = input.selectionStart;
            let end = input.selectionEnd;

            if (end === input.value.length) return;

            input.setSelectionRange(start, end + 1);
            return;
          }
        }

        if (event.key === 'ArrowLeft') {
          if (input.selectionStart === 0) return;

          setCursorPosition(input, input.selectionStart - 1);
          return;
        }

        if (event.key === 'ArrowRight') {
          if (input.selectionStart === input.value.length) return;

          setCursorPosition(input, input.selectionStart + 1);
          return;
        }

        if (event.key === 'Backspace') {
          onBackspace(input);
          return;
        }

        if (event.key === 'Delete') {
          onDelete(input);
          return;
        }

        if (event.key === 'Home') {
          setCursorPosition(input, 0);
          return;
        }

        if (event.key === 'End') {
          setCursorPosition(input, input.value.length);
          return;
        }

         var char = event.key;
        if (char.length > 1) {
          return;
        }

        let start = input.selectionStart;
        let end = input.selectionEnd;
        const text = globalFilter;
        const value = text.substring(0, start) + char + text.substring(end);
        updateText(input, value, start + 1, end);

        return;
      }

      if (shortcutToUpdate) {
        if (formRaccourciRef && formRaccourciRef.current) {
          formRaccourciRef.current.onKeyDown(event);
          return;
        }
      }      

      if (!shortcutsAreTesting || popup.display) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const { shiftKey, altKey, ctrlKey, key, location } = event;

      if (location === 1) {
        return;
      }

     const app = data.applications.find((x) => x.name === selectedApplication);

      const shortcuts = flatMapDeep(app.subfamily.map((x) => x.shortcuts));

      const foundKey = shortcuts.find(
        (u) =>
          u.current.code === key &&
          u.current.shift === shiftKey &&
          u.current.ctrl === ctrlKey &&
          u.current.alt === altKey
      );

      if (foundKey) {
        setPopup({
          display: true,
          title: '',
          text: {
            fr: `Vous avez trouvé le raccourci pour l'action "${foundKey.fr}"`,
            en: `You have found the shortcut for the action "${foundKey.en}"`,
          },
          buttons: [],
          icon: null,
          showIcon: true,
        });
      }
    },

    getItems() {
      return data;
    },

    getIsUpdated() {
      return updated;
    },

    exportJson() {
      const jsonString = JSON.stringify(data);

      this.downloadFile(jsonString, 'export.json', 'application/json');
    },


    exportCSV()  {
        let plainText =  "\uFEFF" + keyboard_jsonToCsv(data, context.language);
        // export en cvs
        console.log("cvs", plainText)
        this.downloadFile(plainText, 'export.csv', 'text/plain');

    },

    exportText() {
      let plainText = '';

      data.applications.forEach((app, index) => {
        plainText += `${lang === 'fr' ? app.fr : app.en}\n`;

        app.subfamily.forEach((sf) => {
          const labelSubfamily = lang === 'fr' ? sf.fr : sf.en;
          plainText += `  ${labelSubfamily}\n`;

          sf.shortcuts.forEach((sc) => {
            const labelShortcut = lang === 'fr' ? sc.fr : sc.en;
            const shortcut = [
              sc.current?.ctrl ? 'CTRL' : '',
              sc.current?.shift ? 'SHIFT' : '',
              sc.current?.alt ? 'ALT' : '',
              sc.current?.code || '',
            ]
              .filter(Boolean)
              .join(' + ');

            plainText += `      ${labelShortcut}\n`;
            plainText += `          ${shortcut}\n`;
          });
        });
      });

      this.downloadFile(plainText, 'export.txt', 'text/plain');
    },

    downloadFile(blobPart, filename, type) {
      const blob = new Blob([blobPart], { type });

      const blobUrl = URL.createObjectURL(blob);

      var link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.click();
    },

    showPopup(popup) {
      setPopup(popup);
    },

    hidePopup() {
      onClosePopup();
    }
  }));

  const columns = useMemo(() => {
    const sortValueFn = (rowA, rowB, columnId) => {
      const a = rowA.original[columnId];
      const b = rowB.original[columnId];

      if (!a) {
        return 1;
      }

      if (!b) {
        return -1;
      }

      // Handle boolean comparison
      if (typeof a === 'boolean' && typeof b === 'boolean') {
        return a === b ? 0 : a ? -1 : 1;
      }

      return a.localeCompare(b, lang, {
        ignorePunctuation: true,
        sensitivity: 'base',
      });
    };

    return [
      {
        accessorKey: 'labelSubfamily',
        header: () => '',
        sortingFn: sortValueFn,
        enableGrouping: true,
      },
      {
        accessorKey: 'labelShortcut',
        header: () => (lang === 'fr' ? 'Libellé' : 'Label'),
        sortingFn: sortValueFn,
      },
      {
        accessorKey: 'shortcut',
        header: lang === 'fr' ? 'Raccourci' : 'Shortcut',
      },
      {
        accessorKey: '',
        header: 'Actions',
        cell: ({ row }) => {
          return (
            <button
              type="button"
              onClick={() => setShortCutToUpdate(row.original)}
            >
              {lang === 'fr' ? 'Modifier' : 'Update'}
            </button>
          );
        },
      },
    ];
  }, [lang]);

  const updateItem = useCallback(
    (application, item) => {
     // Find the selected application
      const appIndex = data.applications.findIndex((a) => a.name === application);
      if (appIndex === -1) return;

      // Find the subfamily by label
      const subFamilyIndex = data.applications[appIndex].subfamily.findIndex(
        (sf) => (lang === 'fr' ? sf.fr : sf.en) === item.labelSubfamily
      );
      if (subFamilyIndex === -1) return;

      // Find the shortcut by key
      const shortcutIndex = data.applications[appIndex].subfamily[
        subFamilyIndex
      ].shortcuts.findIndex((sc) => sc.key === item.key);
      if (shortcutIndex === -1) return;

      // Update the shortcut value
      let current =
        data.applications[appIndex].subfamily[subFamilyIndex].shortcuts[shortcutIndex]
          .current;
      current.ctrl = item.ctrl;
      current.shift = item.shift;
      current.alt = item.alt;
      current.code = item.code;

      // Update state immutably
      setData({ ...data });
      setShortCutToUpdate(undefined);
      setUpdated(true);
    },
    [data, lang]
  );

  const onSubmit = useCallback(
    (itemToUpdate) => {
      const items = [...shortcutsData];

      const itemExists = items.filter(
        (x) =>
          x.code === itemToUpdate.code &&
          x.shift === itemToUpdate.shift &&
          x.alt === itemToUpdate.alt &&
          x.ctrl === itemToUpdate.ctrl
      );

      if (itemExists.length >= 1) {
        setPopup({
          display: true,
          title: '',
          text: {
            fr: `Ce raccourci existe déjà dans l'application ${selectedApplication}.`,
            en: `This shortcut already exists in ${selectedApplication} application`,
          },
          buttons: [],
          icon: null,
          showIcon: true,
        });

        const itemToRevert = items.find(
          (x) => x.labelShortcut === itemToUpdate.labelShortcut
        );

        setShortCutToUpdate(itemToRevert);

        return;
      }

      const duplicateApp = [];
      const duplicate = data.applications.some((app) => {
        if (!app.unique) {
          return false;
        }

        const value = app.subfamily.some((sf) =>
          sf.shortcuts.some(
            (sc) =>
              sc.current.code === itemToUpdate.code &&
              sc.current.shift === itemToUpdate.shift &&
              sc.current.alt === itemToUpdate.alt &&
              sc.current.ctrl === itemToUpdate.ctrl &&
              !(app.name === selectedApplication)
          )
        );

        if (value) {
          duplicateApp.push(app);
        }

        return value;
      });

      if (duplicate) {
        setPopup({
          display: true,
          title: '',
          text: {
            fr:
              duplicateApp.length === 1
                ? `Ce raccourci existe déjà dans une autre application : ${duplicateApp[0].fr}.`
                : `Ce raccourci existe déjà dans les applications : ${duplicateApp
                    .map((x) => x.fr)
                    .join(', ')}.`,
            en:
              duplicateApp.length === 1
                ? `This shortcut already exists in another application: ${duplicateApp.en}`
                : `This shortcut already exists in anothers applications: ${duplicateApp
                    .map((x) => x.fr)
                    .join(', ')}.`,
          },
          buttons: [],
          icon: null,
          showIcon: true,
        });

        const itemToRevert = items.find(
          (x) => x.labelShortcut === itemToUpdate.labelShortcut
        );

        setShortCutToUpdate(itemToRevert);

        return;
      }

      const copyShortcutToApps = [];
      data.applications
        .filter((app) => app.name !== selectedApplication)
        .forEach((app) => {
          if (
            app.subfamily.some((sf) =>
              sf.shortcuts.some((sc) => sc.key === itemToUpdate.key)
            )
          ) {
            copyShortcutToApps.push({
              name: app.name,
              label: lang === 'fr' ? app.fr : app.en,
            });
          }
        });

      if (copyShortcutToApps.length) {
        const labels = copyShortcutToApps.map((x) => x.label).join(', ');
        setPopup({
          display: true,
          title: '',
          text: {
            fr: `Ce raccourci existe aussi dans les applications : ${labels}. Voulez-vous aussi affecter cette nouvelle valeur à ces applications ?`,
            en: `This shortcut also exists in applications: ${labels}. Would you also like to assign this new value to these applications?`,
          },
          buttons: [
            {
              text: {
                fr: 'Oui',
                en: 'Yes',
              },
              onclick: () => {
                copyShortcutToApps.forEach((app) =>
                  updateItem(app.name, itemToUpdate)
                );
                onClosePopup();
              },
            },
            {
              text: {
                fr: 'Non',
                en: 'No',
              },
              onclick: onClosePopup,
            },
          ],
          icon: null,
          showIcon: true,
        });
      }

      updateItem(selectedApplication, itemToUpdate);
    },
    [data, lang, selectedApplication, shortcutsData, updateItem]
  );

  const table = useReactTable({
    data: shortcutsData,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      globalFilter,
      expanded,
      grouping,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'fuzzy',
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    autoResetExpanded: false,
    enableColumnResizing: true,
    enableRowSelection: true,
    columnResizeMode: 'onChange',
  });

  useEffect(() => {
   table.toggleAllRowsExpanded(globalFilter !== '');
  }, [globalFilter, table]);

  const { rows } = table.getRowModel();

  return (
    <div className="container-raccourcis">
      <div className="container-raccourcis-header">
        <div className="group">
          <label htmlFor="lstApplications">Applications</label>
          <select
            id="lstApplications"
            value={selectedApplication}
            onChange={(e) => setSelectedApplication(e.target.value)}
          >
            {applications.map((app, index) => (
              <option key={app.name} value={app.name}>
                {lang === 'fr' ? app.fr : app.en}
              </option>
            ))}
          </select>
        </div>
        <div className="group">
          <div className="checkbox-shortcut">
            <IndeterminateCheckbox
              id="chkShortcuts"
              checked={shortcutsAreTesting}
              onChange={(event) => setShortcutsAreTesting(event.target.checked)}
            />
            <label htmlFor="chkShortcuts">
              {lang === 'fr' ? 'Tester les raccourcis' : 'Testing shortcuts'}
            </label>
          </div>
        </div>

        <div className="search">
          <DebouncedInput
            id="txtRechercheRaccourcis"
            type="text"
            value={globalFilter ?? ''}
            onChange={(value) => setGlobalFilter(String(value))}
            placeholder={lang === 'fr' ? 'Rechercher' : 'Search'}
          />
        </div>
      </div>
      <div className="container-raccourcis-body">
        <div className="table">
          <table>
            <thead
              style={{
                display: 'grid',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: 'black',
              }}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id}>
                      <div
                        {...{
                          className: header.column.getCanSort() ? 'sort' : '',
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        <SortIcon order={header.column.getIsSorted()} />
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {rows.map((row) => {
                return (
                  <tr
                    key={row.id}
                    style={{
                      display: 'flex',

                      width: '100%',
                      background: row.getIsGrouped() ? '' : undefined,
                      fontWeight: row.getIsGrouped() ? 'bold' : undefined,
                      cursor: row.getIsGrouped() ? 'pointer' : undefined,
                    }}
                    onClick={
                      row.getIsGrouped() && row.getCanExpand()
                        ? row.getToggleExpandedHandler()
                        : undefined
                    }
                  >
                    {row.getVisibleCells().map((cell, index) => {
                      if (row.getIsGrouped()) {
                        if (index !== 0) {
                          return <td key={cell.id}></td>;
                        }

                        return (
                          <td
                            key={cell.id}
                            colSpan={6}
                            style={{ width: '100%' }}
                          >
                            <>
                              <span
                                style={{ marginRight: 8 }}
                                aria-hidden="true"
                              >
                                {row.getIsExpanded() ? '▼' : '▶'}
                              </span>
                              <span className="sr-only">
                                {row.getIsExpanded()
                                  ? lang === 'fr'
                                    ? 'Réduire'
                                    : 'Collapse'
                                  : lang === 'fr'
                                  ? 'Développer'
                                  : 'Expand'}
                              </span>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                              <span style={{ marginLeft: 8, color: '' }}>
                                ({row.subRows.length})
                              </span>
                            </>
                          </td>
                        );
                      }

                      return (
                        <td key={cell.id}>
                          {index === 0
                            ? ''
                            : flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="container-raccourcis-footer">
        <FormRaccourci
          ref={formRaccourciRef}
          lang={lang}
          item={shortcutToUpdate}
          onClose={() => setShortCutToUpdate(undefined)}
          onSubmit={onSubmit}
        ></FormRaccourci>
      </div>
      <div>
        <SimplePopup
          display={popup.display}
          title={popup.title}
          text={popup.text}
          onCancel={onClosePopup}
          buttons={popup.buttons}
          showIcon={popup.showIcon}
          icon={popup.icon}
        />
      </div>
    </div>
  );
});

export default Raccourcis;
